import { useState, type SyntheticEvent } from 'react';
import { useNavigate, Link, Navigate } from 'react-router';
import { useAuth } from '@/contexts/AuthContext';
import { signUpRandonneur } from '@/services/auth.service';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import { MdPerson, MdMail, MdLock, MdDirectionsBike, MdBadge } from 'react-icons/md';

function isValidCpf(cpf: string): boolean {
  const digits = cpf.replaceAll(/\D/g, '');
  if (digits.length !== 11 || /^(\d)\1+$/.test(digits)) return false;
  let sum = 0;
  for (let i = 0; i < 9; i++) sum += Number.parseInt(digits[i]) * (10 - i);
  let remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== Number.parseInt(digits[9])) return false;
  sum = 0;
  for (let i = 0; i < 10; i++) sum += Number.parseInt(digits[i]) * (11 - i);
  remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  return remainder === Number.parseInt(digits[10]);
}

function formatCpf(value: string): string {
  const digits = value.replaceAll(/\D/g, '').slice(0, 11);
  return digits
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
}

export default function CadastroPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const [nome, setNome] = useState('');
  const [cpf, setCpf] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [serverError, setServerError] = useState('');

  if (isAuthenticated) {
    return <Navigate to="/perfil" replace />;
  }

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!nome.trim()) errs.nome = 'Nome é obrigatório';
    if (!isValidCpf(cpf)) errs.cpf = 'CPF inválido';
    if (!email) errs.email = 'E-mail é obrigatório';
    if (password.length < 8) errs.password = 'A senha deve ter pelo menos 8 caracteres';
    if (password !== confirmPassword) errs.confirmPassword = 'As senhas não coincidem';
    return errs;
  };

  const handleSubmit = async (e: SyntheticEvent) => {
    e.preventDefault();
    setServerError('');
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setErrors({});

    try {
      setIsLoading(true);
      await signUpRandonneur({ nome, cpf: cpf.replaceAll(/\D/g, ''), email, password });
      navigate('/confirmar-email', { state: { email } });
    } catch (err: unknown) {
      const name = (err as { name?: string })?.name ?? '';
      if (name === 'UsernameExistsException') {
        setServerError('E-mail já cadastrado. Tente fazer login ou recupere sua senha.');
      } else {
        setServerError('Não foi possível criar a conta. Tente novamente.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-brand-600 to-brand-800 px-4 py-8">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-brand-100 rounded-full mb-4">
              <MdDirectionsBike className="text-3xl text-brand-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">RandoBR</h1>
            <p className="text-gray-600">Criar conta de Randonneur</p>
          </div>

          {serverError && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {serverError}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="nome" className="block text-sm font-medium text-gray-700 mb-2">Nome completo</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MdPerson className="text-gray-400 text-xl" />
                </div>
                <input id="nome" type="text" value={nome} onChange={(e) => setNome(e.target.value)}
                  className="input-field pl-10" placeholder="Seu nome completo"
                  disabled={isLoading} autoComplete="name" />
              </div>
              {errors.nome && <p className="mt-1 text-xs text-red-600">{errors.nome}</p>}
            </div>

            <div>
              <label htmlFor="cpf" className="block text-sm font-medium text-gray-700 mb-2">CPF</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MdBadge className="text-gray-400 text-xl" />
                </div>
                <input id="cpf" type="text" inputMode="numeric" value={cpf}
                  onChange={(e) => setCpf(formatCpf(e.target.value))}
                  className="input-field pl-10" placeholder="000.000.000-00"
                  disabled={isLoading} maxLength={14} />
              </div>
              {errors.cpf && <p className="mt-1 text-xs text-red-600">{errors.cpf}</p>}
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">E-mail</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MdMail className="text-gray-400 text-xl" />
                </div>
                <input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  className="input-field pl-10" placeholder="seu@email.com"
                  disabled={isLoading} autoComplete="email" />
              </div>
              {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email}</p>}
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">Senha</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MdLock className="text-gray-400 text-xl" />
                </div>
                <input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                  className="input-field pl-10" placeholder="Mínimo 8 caracteres"
                  disabled={isLoading} autoComplete="new-password" />
              </div>
              {errors.password && <p className="mt-1 text-xs text-red-600">{errors.password}</p>}
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">Confirmar senha</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MdLock className="text-gray-400 text-xl" />
                </div>
                <input id="confirmPassword" type="password" value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="input-field pl-10" placeholder="Repita a senha"
                  disabled={isLoading} autoComplete="new-password" />
              </div>
              {errors.confirmPassword && <p className="mt-1 text-xs text-red-600">{errors.confirmPassword}</p>}
            </div>

            <button type="submit" disabled={isLoading} className="btn-primary w-full flex items-center justify-center gap-2 mt-2">
              {isLoading ? (
                <>
                  <LoadingSpinner size="sm" />
                  <span>Criando conta...</span>
                </>
              ) : (
                <span>Criar conta</span>
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-500">
              Já tem conta?{' '}
              <Link to="/login" className="text-brand-600 hover:text-brand-700 font-medium">
                Fazer login
              </Link>
            </p>
          </div>
        </div>

        <p className="text-center text-white text-sm mt-8">&copy; 2026 RandoBR. Todos os direitos reservados.</p>
      </div>
    </div>
  );
}
